import { Spinner_v2 as Spinner } from '@mindly/ui-components';
import { ExpressCheckoutElement } from '@stripe/react-stripe-js';
import {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import { useCallback, useState } from 'react';
import useToast from 'src/context/ToastContext/ToastContext';
import { useLog } from 'src/shared/hooks/useLog';

type ExpressCheckoutProps = {
  onReady?: (event: StripeExpressCheckoutElementReadyEvent) => void;
  confirmExpressCheckout: () => Promise<void>;
  onSuccess: () => void;
  onLoadError?: (error: unknown) => void;
  onClick?: (event: StripeExpressCheckoutElementClickEvent) => void;
  showError?: boolean;
};

const expressCheckoutOptions: StripeExpressCheckoutElementOptions = {
  buttonType: {
    applePay: 'plain',
    googlePay: 'plain',
  },
  layout: {
    maxColumns: 1,
    maxRows: 2,
  },
  paymentMethods: {
    applePay: 'always',
    googlePay: 'always',
    link: 'never',
    paypal: 'never',
    klarna: 'never',
    amazonPay: 'never',
  },
};

const ExpressCheckout: React.FC<ExpressCheckoutProps> = ({
  onReady,
  onSuccess,
  confirmExpressCheckout,
  onLoadError,
  onClick,
  showError = false,
}) => {
  const [expressCheckoutLoading, setExpressCheckoutLoading] = useState<boolean>(true);
  const [expressCheckoutError, setExpressCheckoutError] = useState<string | null>(null);
  const { logError } = useLog();
  const { showToast } = useToast();

  const onExpressCheckoutReady = useCallback(
    (event: StripeExpressCheckoutElementReadyEvent) => {
      setExpressCheckoutLoading(false);
      onReady?.(event);

      if (!event?.availablePaymentMethods) {
        setExpressCheckoutError('No available payment methods');
        onLoadError?.('No available payment methods');
      }
    },
    [onReady, logError, onLoadError]
  );

  const onExpressCheckoutConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      try {
        await confirmExpressCheckout();
        onSuccess();
      } catch (error) {
        logError(error);
        showToast({ error });
      }
    },
    [confirmExpressCheckout, logError, showToast, onSuccess]
  );

  const onExpressCheckoutClick = useCallback(
    (event: StripeExpressCheckoutElementClickEvent) => {
      onClick?.(event);
      event?.resolve();
    },
    [onClick]
  );

  if (expressCheckoutError && showError) {
    return <div className="text-center text-[#F24E1E]">{expressCheckoutError}</div>;
  } else if (expressCheckoutError) {
    return null;
  }

  return (
    <>
      {expressCheckoutLoading && <Spinner className="mx-auto" />}
      <ExpressCheckoutElement
        onConfirm={onExpressCheckoutConfirm}
        onReady={onExpressCheckoutReady}
        onClick={onExpressCheckoutClick}
        options={expressCheckoutOptions}
      />
    </>
  );
};

export default ExpressCheckout;
